/// <amd-module name="MarkdownEditor/Components/MarkdownComponent" />
import { IMarkdownComponent, IMarkdownComponentProps } from "MarkdownEditor/Components/MarkdownComponent.Props";
import { MarkdownEditor } from "MarkdownEditor/Components/MarkdownEditor";
import { MarkdownTabBar } from "MarkdownEditor/Components/MarkdownTabBar";
import { ILegacyMentionSerivce, LegacyMentionSerivceName } from "MarkdownEditor/LegacyMentionService";
import * as React from "react";
import { shallowCompare } from "VSS/Core/Util/Object";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";

export enum MarkdownComponentMode {
    Preview,
    Edit
}

export interface IMarkdownComponentState {
    mode: MarkdownComponentMode;
    isTextboxFocused: boolean;
    isReady: boolean;
    customPreviewContent?: JSX.Element;
}

export class MarkdownComponent extends VssComponent<IMarkdownComponentProps, IMarkdownComponentState> implements IMarkdownComponent {
    private _readyPromise: Promise<string> | null = null;
    private _initialValue: string;
    private _editorRef: MarkdownEditor;

    constructor(props: IMarkdownComponentProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            mode: MarkdownComponentMode.Edit,
            isTextboxFocused: false,
            isReady: !(props.enableMentions && props.value)
        };

        if (props.enableMentions && props.value) {
            const mentionService = context.pageContext.getService<ILegacyMentionSerivce>(LegacyMentionSerivceName);
            this._initialValue = props.value;
            this._readyPromise = mentionService.translateStorageKeysToDisplayNamesOfPersonMentions(props.value);
            this._readyPromise.then((decoratedContent: string) => {
                this.setState({ isReady: true });
                this.props.onTextChanged && this.props.onTextChanged(decoratedContent);
            });
        }
    }

    public render(): JSX.Element {
        const { isTextboxFocused, mode, isReady } = this.state;
        const { className, isActive, onBlur, onFocus, disabled, showToolbar } = this.props;

        return (
            <FocusWithin onBlur={onBlur} onFocus={onFocus}>
                {(focusStatus: IFocusWithinStatus) => (
                    <div
                        className={css("markdown-component", "flex-grow", isTextboxFocused && "is-focused", isActive && "is-active", className)}
                        onFocus={focusStatus.onFocus}
                        onBlur={focusStatus.onBlur}
                    >
                        {isActive && (
                            <MarkdownTabBar
                                selectedTab={mode === MarkdownComponentMode.Preview ? "preview" : "edit"}
                                togglePreview={this._togglePreview}
                            />
                        )}

                        {mode === MarkdownComponentMode.Edit ? (
                            <div className="markdown-editor-container" onFocus={this._onTextFocus} onBlur={this._onTextBlur}>
                                <MarkdownEditor
                                    {...this.props}
                                    disabled={isReady ? disabled : false}
                                    showToolbar={isReady ? showToolbar : false}
                                    minHeight={isActive ? 65 : 40}
                                    onFocus={undefined}
                                    onBlur={undefined}
                                    ref={this._setEditor}
                                />
                            </div>
                        ) : (
                            <div className="markdown-preview-container">{this._renderPreview()}</div>
                        )}
                    </div>
                )}
            </FocusWithin>
        );
    }
    public shouldComponentUpdate(newProps: IMarkdownComponentProps, newState: IMarkdownComponentState): boolean {
        return !shallowCompare(newProps, this.props) || !shallowCompare(newState, this.state);
    }

    public isEmpty(): boolean {
        if (this._editorRef) {
            return this._editorRef.isEmpty();
        }

        const { value } = this.props;
        return !value || value.length === 0;
    }

    public getValue(translateMentions: boolean): Promise<string> {
        const { value, enableMentions } = this.props;
        const { isReady } = this.state;

        if (!value) {
            return Promise.resolve("");
        }

        if (enableMentions) {
            if (translateMentions) {
                const mentionService = this.context.pageContext.getService<ILegacyMentionSerivce>(LegacyMentionSerivceName);
                return mentionService.translateDisplayNamesToStorageKeysOfPersonMentions(value);
            }

            if (!isReady && value === this._initialValue) {
                return this._readyPromise!;
            }
        }

        return Promise.resolve(value);
    }

    private _setEditor = (editor: MarkdownEditor) => {
        this._editorRef = editor;
    };

    private _onTextFocus = () => {
        this.setState({ isTextboxFocused: true });
    };

    private _onTextBlur = () => {
        this.setState({ isTextboxFocused: false });
    };

    private _togglePreview = () => {
        const newState: Partial<IMarkdownComponentState> = {
            mode: this.state.mode === MarkdownComponentMode.Preview ? MarkdownComponentMode.Edit : MarkdownComponentMode.Preview
        };
        if (newState.mode === MarkdownComponentMode.Edit) {
            newState.customPreviewContent = undefined;
        }
        this.setState(newState as IMarkdownComponentState);
    };

    private _renderPreview = (): JSX.Element => {
        const { value = "", renderPreview } = this.props;

        if (renderPreview) {
            if (!this.state.customPreviewContent) {
                renderPreview(value).then(customPreviewContent => this.setState({ customPreviewContent }));
            }

            return this.state.customPreviewContent || <></>;
        } else {
            return <>{value}</>;
        }
    };
}

VssComponent.register("ms.vss-markdown.markdown-component", MarkdownComponent);
